"use client"

import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { ArrowLeft, Users, Pin, PinOff, Lock, ChevronLeft, ChevronRight, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import Link from "next/link"
import { WINDOW_REGISTRY, type WindowConfig } from "@/windows/window-registry"
import { useSearchParams } from "next/navigation"
import { GalaxyStarsBackground } from "@/components/galaxy-stars-background"
import { PathwayMobileMenu } from "@/components/pathway-mobile-menu"
import { OnboardingWidgetHost } from "@/components/OnboardingWidgetHost"

type RecordingState =
  | "idle"
  | "requesting-permission"
  | "ready"
  | "recording"
  | "processing"
  | "waiting-llm"
  | "generating-voice"
  | "playing-response"

interface WindowState extends WindowConfig {
  position: { x: number; y: number; scale: number; depth: number }
  isPinned: boolean
}

const LOGGED_IN_DEFAULT_PINS = [
  "features",
  "chat-info",
  "pricing",
  "price-chart",
  "chatbox",
  "domain-management",
  "user-dashboard",
  "windows-explorer",
]
const GUEST_DEFAULT_PINS = ["features", "pricing", "windows-explorer"]

function getDefaultPins(loggedIn: boolean): string[] {
  return loggedIn ? LOGGED_IN_DEFAULT_PINS : GUEST_DEFAULT_PINS
}

function getPinnedWindowsStorageKey(): string {
  if (typeof window === "undefined") return "pathway-pinned-windows:guest"

  const loggedIn = localStorage.getItem("isLoggedIn") === "true"
  const phone = localStorage.getItem("userPhone")
  if (loggedIn && phone) return `pathway-pinned-windows:${phone}`
  return "pathway-pinned-windows:guest"
}

function normalizePinnedWindows(pins: string[]): string[] {
  return pins
    .map((id) => (id === "domain-conversations" ? "chat-info" : id))
    .filter((id, index, arr) => arr.indexOf(id) === index)
}

function loadPinnedWindowsFromStorage(loggedIn: boolean): string[] {
  try {
    const saved = localStorage.getItem(getPinnedWindowsStorageKey())
    if (!saved) return getDefaultPins(loggedIn)

    const parsed = JSON.parse(saved) as string[]
    if (!Array.isArray(parsed)) return getDefaultPins(loggedIn)

    return normalizePinnedWindows(parsed)
  } catch {
    return getDefaultPins(loggedIn)
  }
}

function persistPinnedWindows(pins: string[]): void {
  try {
    localStorage.setItem(getPinnedWindowsStorageKey(), JSON.stringify(pins))
  } catch (error) {
    console.error("Error saving pinned windows:", error)
  }
}

export default function PathwayPage() {
  const searchParams = useSearchParams()
  const [currentWindowId, setCurrentWindowId] = useState<string>(() => {
    // Find first active window as default, fallback to mobile-auth if sign-in is deactivated
    const firstActiveWindow = WINDOW_REGISTRY.find((w) => w.active !== false)
    return firstActiveWindow?.id || "mobile-auth"
  })
  const [aiVolumeLevel, setAiVolumeLevel] = useState(0)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [pendingTutorialId, setPendingTutorialId] = useState<string | null>(null)
  const [windows, setWindows] = useState<WindowState[]>(() => {
    const loggedIn = typeof window !== "undefined" && localStorage.getItem("isLoggedIn") === "true"
    const initialPins = loadPinnedWindowsFromStorage(loggedIn)

    return WINDOW_REGISTRY.filter((window) => window.active !== false).map((window) => ({
      ...window,
      position: { ...window.initialPosition },
      isPinned: initialPins.includes(window.id),
    }))
  })
  const [pinnedWindows, setPinnedWindows] = useState<string[]>(() => {
    const loggedIn = typeof window !== "undefined" && localStorage.getItem("isLoggedIn") === "true"
    return loadPinnedWindowsFromStorage(loggedIn)
  })
  const [pinnedWindowsPositions, setPinnedWindowsPositions] = useState<Record<string, { x: number; y: number }>>({})
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [isNavigationLocked, setIsNavigationLocked] = useState(false)
  const [draggedPinId, setDraggedPinId] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null)
  const hasDraggedRef = useRef<boolean>(false)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const voiceAudioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const isRecordingRef = useRef(false)
  const audioContextRef = useRef<AudioContext | null>(null)
  const hasProcessedUrlParam = useRef(false)

  useEffect(() => {
    const handleStorageChange = () => {
      const loginStatus = localStorage.getItem("isLoggedIn") === "true"
      setIsLoggedIn(loginStatus)
    }

    handleStorageChange()
    window.addEventListener("storage", handleStorageChange)
    window.addEventListener("loginStatusChanged", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener("loginStatusChanged", handleStorageChange)
    }
  }, [])

  // Load pinned windows from localStorage when login status changes
  useEffect(() => {
    const pins = loadPinnedWindowsFromStorage(isLoggedIn)
    setPinnedWindows(pins)
    setWindows((prev) => prev.map((w) => ({ ...w, isPinned: pins.includes(w.id) })))
  }, [isLoggedIn])

  useEffect(() => {
    const windowParam = searchParams.get("window")
    if (windowParam) {
      // Legacy: domain-conversations window was replaced by chat-info
      const resolvedWindowParam =
        windowParam === "domain-conversations" ? "chat-info" : windowParam

      // Redirect logged-in users away from mobile-auth to user-dashboard
      if (resolvedWindowParam === "mobile-auth" && isLoggedIn) {
        setCurrentWindowId("user-dashboard")
        setWindows((prev) =>
          prev.map((window) => {
            if (window.id === "user-dashboard") {
              return { ...window, position: { x: 0, y: 0, scale: 1.0, depth: 0 } }
            }
            return window
          }),
        )
        return
      }

      const targetWindow = WINDOW_REGISTRY.find((w) => w.id === resolvedWindowParam && w.active !== false)
      if (targetWindow) {
        setCurrentWindowId(resolvedWindowParam)
        setWindows((prev) =>
          prev.map((window) => {
            if (window.id === resolvedWindowParam) {
              return { ...window, position: { x: 0, y: 0, scale: 1.0, depth: 0 } }
            }
            return window
          }),
        )
      }
    }
  }, [searchParams, isLoggedIn])

  const DEFAULT_LOCKED_WINDOWS = ["user-dashboard", "user-settings"]

  const visibleWindows = useMemo(() => {
    // Filter out inactive windows (active defaults to true if not specified)
    // Also hide mobile-auth window when user is logged in
    return windows.filter((window) => window.active !== false && !(window.id === "mobile-auth" && isLoggedIn))
  }, [windows, isLoggedIn])

  const navigateToPreviousWindow = () => {
    if (isNavigationLocked) return
    const currentIndex = visibleWindows.findIndex((w) => w.id === currentWindowId)
    if (visibleWindows.length > 1) {
      const previousIndex = currentIndex === 0 ? visibleWindows.length - 1 : currentIndex - 1
      navigateToWindow(visibleWindows[previousIndex].id)
    }
  }

  const navigateToNextWindow = () => {
    if (isNavigationLocked) return
    const currentIndex = visibleWindows.findIndex((w) => w.id === currentWindowId)
    if (visibleWindows.length > 1) {
      const nextIndex = currentIndex === visibleWindows.length - 1 ? 0 : currentIndex + 1
      navigateToWindow(visibleWindows[nextIndex].id)
    }
  }

  const navigateToWindow = async (windowId: string) => {
    if (isNavigationLocked) return

    const targetWindow = visibleWindows.find((w) => w.id === windowId)
    if (!targetWindow || windowId === currentWindowId || isTransitioning) return

    // Allow navigation even if locked (game-like behavior)

    setIsTransitioning(true)
    // Play movement sound
    try {
      if (audioContextRef.current) {
        const oscillator = audioContextRef.current.createOscillator()
        const gainNode = audioContextRef.current.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContextRef.current.destination)
        // Adjusted frequency to a pleasant middle ground (120Hz->100Hz) - audible on speakers but not too high
        oscillator.frequency.setValueAtTime(120, audioContextRef.current.currentTime)
        oscillator.frequency.exponentialRampToValueAtTime(100, audioContextRef.current.currentTime + 0.5)
        gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime)
        // Increase peak volume slightly and sustain a bit higher
        gainNode.gain.linearRampToValueAtTime(0.35, audioContextRef.current.currentTime + 0.05)
        gainNode.gain.exponentialRampToValueAtTime(0.06, audioContextRef.current.currentTime + 0.5)
        oscillator.type = "sine"
        oscillator.start(audioContextRef.current.currentTime)
        oscillator.stop(audioContextRef.current.currentTime + 0.5)
      }
    } catch {}

    setWindows((prev) =>
      prev.map((window) => {
        if (window.id === currentWindowId) {
          return { ...window, position: { ...window.initialPosition } }
        } else if (window.id === windowId) {
          return { ...window, position: { x: 0, y: 0, scale: 1.0, depth: 0 } }
        }
        return window
      }),
    )

    await new Promise((resolve) => setTimeout(resolve, 600))

    setCurrentWindowId(windowId)
    setIsTransitioning(false)
  }

  const togglePin = (windowId: string) => {
    const window = visibleWindows.find((w) => w.id === windowId)
    if (!window) return

    let nextPins: string[] | null = null

    if (pinnedWindows.includes(windowId)) {
      nextPins = pinnedWindows.filter((id) => id !== windowId)
      setPinnedWindows(nextPins)
      setWindows((prev) => prev.map((w) => (w.id === windowId ? { ...w, isPinned: false } : w)))
    } else if (pinnedWindows.length < 10) {
      nextPins = [...pinnedWindows, windowId]
      setPinnedWindows(nextPins)
      setWindows((prev) => prev.map((w) => (w.id === windowId ? { ...w, isPinned: true } : w)))
    }

    if (nextPins) {
      persistPinnedWindows(nextPins)
    }
  }

  const navigateToPinnedWindow = (windowId: string) => {
    navigateToWindow(windowId)
  }

  useEffect(() => {
    // Disable scroll-based navigation entirely
    const handleWheelEvent = (e: WheelEvent) => {
      return
    }

    window.addEventListener("wheel", handleWheelEvent, { passive: true })
    return () => window.removeEventListener("wheel", handleWheelEvent)
  }, [])

  // Initialize audio context for movement sound (first interaction)
  useEffect(() => {
    const initAudio = () => {
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
        }
      } catch {}
    }
    const onInteract = () => {
      initAudio()
      document.removeEventListener("click", onInteract)
      document.removeEventListener("keydown", onInteract)
    }
    document.addEventListener("click", onInteract)
    document.addEventListener("keydown", onInteract)
    return () => {
      document.removeEventListener("click", onInteract)
      document.removeEventListener("keydown", onInteract)
    }
  }, [])

  // Handle dragging pinned windows
  useEffect(() => {
    if (!draggedPinId || !dragStartPosRef.current) return

    const handleMouseMove = (e: MouseEvent) => {
      if (draggedPinId && dragStartPosRef.current) {
        hasDraggedRef.current = true
        // Calculate new position relative to the centered container
        const containerCenterX = window.innerWidth / 2
        const containerTop = 16 // top-4 = 1rem = 16px
        const newX = e.clientX - containerCenterX
        const newY = e.clientY - containerTop
        setPinnedWindowsPositions((prev) => ({
          ...prev,
          [draggedPinId]: { x: newX, y: newY },
        }))
      }
    }

    const handleMouseUp = () => {
      // Small delay to allow click to fire if no drag occurred
      setTimeout(() => {
        setDraggedPinId(null)
        dragStartPosRef.current = null
        hasDraggedRef.current = false
      }, 100)
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [draggedPinId])

  const currentWindow = visibleWindows.find((w) => w.id === currentWindowId)
  const CurrentWindowComponent = currentWindow?.component

  return (
    <div className="min-h-screen overflow-hidden relative">
      <GalaxyStarsBackground />

      {/* Static Pathway Background */}
      <div className="fixed inset-0 overflow-hidden">

        {/* All Pathway Windows in Background */}
        {visibleWindows.map((window, index) => {
          const isActive = window.id === currentWindowId
          const opacityEffect = isActive ? 0 : Math.max(0.3, 1 - window.position.depth * 0.15)

          return (
            <div
              key={window.id}
              className={`absolute transition-all duration-600 ease-out cursor-pointer hover:scale-105`}
              style={{
                left: `${50 + window.position.x}%`,
                top: `${40 + window.position.y}%`,
                transform: `translate(-50%, -50%) scale(${Math.max(0.05, window.position.scale)})`,
                opacity: opacityEffect,
                zIndex: isActive ? 0 : 20 - window.position.depth,
                pointerEvents: "auto",
              }}
              onClick={() => {
                const isLocked =
                  (window.requiresAuth && !isLoggedIn) || (window.id === "mobile-auth" && isLoggedIn)
                // If locked, do not navigate
                if (!isActive && !isLocked) navigateToWindow(window.id)
              }}
            >
              <div className="relative">
                <div className="absolute -top-2 -left-2 z-10">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      togglePin(window.id)
                    }}
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-sm
                      ${
                        window.isPinned
                          ? "bg-[#01ADEF] text-white shadow-lg shadow-[#01ADEF]/50 border border-white/20"
                          : "bg-white/20 text-white hover:bg-[#01ADEF] hover:text-white border border-white/30"
                      }
                    `}
                  >
                    {window.isPinned ? <PinOff size={14} /> : <Pin size={14} />}
                  </button>
                </div>

                {(window.requiresAuth && !isLoggedIn) || (window.id === "mobile-auth" && isLoggedIn) ? (
                  <div className="absolute -top-2 -right-2 z-10">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-orange-500 text-white shadow-lg border border-white/20">
                      <Lock size={14} />
                    </div>
                  </div>
                ) : null}

                <div
                  className={`border-3 bg-white/10 backdrop-blur-sm rounded-lg p-8 min-w-[250px] min-h-[180px] flex flex-col justify-center items-center shadow-2xl transition-all duration-300 hover:bg-white/20 border-white/60 relative`}
                  style={{
                    boxShadow: `0 0 40px rgba(1, 173, 239, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.1)`,
                    borderColor: index % 2 === 0 ? "#01ADEF" : "#ffffff",
                    filter:
                      (window.requiresAuth && !isLoggedIn) || (window.id === "mobile-auth" && isLoggedIn)
                        ? "grayscale(100%) brightness(0.8)"
                        : "none",
                  }}
                  onClick={() => {
                    const isLocked =
                      (window.requiresAuth && !isLoggedIn) || (window.id === "mobile-auth" && isLoggedIn)
                    // If locked, card click does not navigate
                    if (!isActive && !isLocked) navigateToWindow(window.id)
                  }}
                >
                  <div className="text-center">
                    <h3 className="font-bold text-xl mb-3 text-white">{window.title}</h3>
                    <p className="text-sm leading-relaxed text-white/90">{window.description}</p>
                    {((window.requiresAuth && !isLoggedIn) || (window.id === "mobile-auth" && isLoggedIn)) && (
                      <div className="mt-3 text-orange-300 text-xs">🔒 Sign in required</div>
                    )}
                  </div>
                  {((window.requiresAuth && !isLoggedIn) || (window.id === "mobile-auth" && isLoggedIn)) && (
                    <div className="absolute inset-0 bg-black/30 rounded-lg pointer-events-none" />
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <PathwayMobileMenu
        open={mobileMenuOpen}
        onOpenChange={setMobileMenuOpen}
        isLoggedIn={isLoggedIn}
        currentWindowId={currentWindowId}
        windows={visibleWindows}
        pinnedWindowIds={pinnedWindows}
        isNavigationLocked={isNavigationLocked}
        onNavigateToWindow={navigateToWindow}
        onLogout={() => {
          try {
            localStorage.setItem("isLoggedIn", "false")
            localStorage.removeItem("userPhone")
            window.dispatchEvent(new Event("loginStatusChanged"))
          } catch {}
          setIsLoggedIn(false)
          navigateToWindow("mobile-auth")
        }}
      />

      {/* Header — desktop only; mobile uses side menu */}
      <div className="absolute top-4 left-4 z-20 hidden md:block">
        <Link href="/home">
          <button
            className="group relative px-4 py-2 rounded-lg font-medium text-white transition-all duration-300 ease-out overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: `
                0 8px 32px rgba(1, 173, 239, 0.3),
                0 4px 16px rgba(0, 0, 0, 0.2),
                inset 0 2px 4px rgba(255, 255, 255, 0.3),
                inset 0 -2px 4px rgba(0, 0, 0, 0.1)
              `,
              transform: 'translateY(0)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)'
              e.currentTarget.style.boxShadow = `
                0 12px 40px rgba(1, 173, 239, 0.4),
                0 6px 20px rgba(0, 0, 0, 0.3),
                inset 0 2px 4px rgba(255, 255, 255, 0.4),
                inset 0 -2px 4px rgba(0, 0, 0, 0.1),
                0 0 20px rgba(1, 173, 239, 0.5)
              `
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)'
              e.currentTarget.style.boxShadow = `
                0 8px 32px rgba(1, 173, 239, 0.3),
                0 4px 16px rgba(0, 0, 0, 0.2),
                inset 0 2px 4px rgba(255, 255, 255, 0.3),
                inset 0 -2px 4px rgba(0, 0, 0, 0.1)
              `
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(1px) scale(0.98)'
              e.currentTarget.style.boxShadow = `
                0 4px 16px rgba(1, 173, 239, 0.2),
                0 2px 8px rgba(0, 0, 0, 0.2),
                inset 0 2px 4px rgba(0, 0, 0, 0.2)
              `
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)'
              e.currentTarget.style.boxShadow = `
                0 12px 40px rgba(1, 173, 239, 0.4),
                0 6px 20px rgba(0, 0, 0, 0.3),
                inset 0 2px 4px rgba(255, 255, 255, 0.4),
                inset 0 -2px 4px rgba(0, 0, 0, 0.1),
                0 0 20px rgba(1, 173, 239, 0.5)
              `
            }}
          >
            <span className="relative z-10 flex items-center gap-1 md:gap-2">
              <ArrowLeft size={14} className="md:w-4 md:h-4 transition-transform duration-300 group-hover:-translate-x-1" />
              <span className="hidden sm:inline">صفحه اصلی</span>
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform -skew-x-12 group-hover:translate-x-full" style={{ transition: 'opacity 0.3s, transform 0.6s' }} />
          </button>
        </Link>
      </div>

      {/* Top Right Controls — desktop only; mobile uses side menu */}
      <div className="absolute top-4 right-4 z-30 hidden md:flex items-center gap-3">
        {!isLoggedIn ? (
          <button
            className="group relative px-4 py-2 rounded-lg font-medium text-white transition-all duration-300 ease-out overflow-hidden"
            onClick={() => navigateToWindow("mobile-auth")}
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: `
                0 8px 32px rgba(1, 173, 239, 0.3),
                0 4px 16px rgba(0, 0, 0, 0.2),
                inset 0 2px 4px rgba(255, 255, 255, 0.3),
                inset 0 -2px 4px rgba(0, 0, 0, 0.1)
              `,
              transform: 'translateY(0)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)'
              e.currentTarget.style.boxShadow = `
                0 12px 40px rgba(1, 173, 239, 0.4),
                0 6px 20px rgba(0, 0, 0, 0.3),
                inset 0 2px 4px rgba(255, 255, 255, 0.4),
                inset 0 -2px 4px rgba(0, 0, 0, 0.1),
                0 0 20px rgba(1, 173, 239, 0.5)
              `
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)'
              e.currentTarget.style.boxShadow = `
                0 8px 32px rgba(1, 173, 239, 0.3),
                0 4px 16px rgba(0, 0, 0, 0.2),
                inset 0 2px 4px rgba(255, 255, 255, 0.3),
                inset 0 -2px 4px rgba(0, 0, 0, 0.1)
              `
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(1px) scale(0.98)'
              e.currentTarget.style.boxShadow = `
                0 4px 16px rgba(1, 173, 239, 0.2),
                0 2px 8px rgba(0, 0, 0, 0.2),
                inset 0 2px 4px rgba(0, 0, 0, 0.2)
              `
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)'
              e.currentTarget.style.boxShadow = `
                0 12px 40px rgba(1, 173, 239, 0.4),
                0 6px 20px rgba(0, 0, 0, 0.3),
                inset 0 2px 4px rgba(255, 255, 255, 0.4),
                inset 0 -2px 4px rgba(0, 0, 0, 0.1),
                0 0 20px rgba(1, 173, 239, 0.5)
              `
            }}
          >
            <span className="relative z-10">ورود</span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform -skew-x-12 group-hover:translate-x-full" style={{ transition: 'opacity 0.3s, transform 0.6s' }} />
          </button>
        ) : (
          <>
            <div 
              className="px-2 py-0.5 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium text-green-100 transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(34, 197, 94, 0.2) 100%)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(34, 197, 94, 0.4)',
                boxShadow: `
                  0 8px 32px rgba(34, 197, 94, 0.3),
                  0 4px 16px rgba(0, 0, 0, 0.2),
                  inset 0 2px 4px rgba(255, 255, 255, 0.3),
                  inset 0 -2px 4px rgba(0, 0, 0, 0.1)
                `,
              }}
            >
              وارد شده اید
            </div>
            <button
              className="group relative px-2 py-1.5 md:px-4 md:py-2 rounded-lg text-sm md:text-base font-medium text-white transition-all duration-300 ease-out overflow-hidden"
              onClick={() => {
                try {
                  localStorage.setItem("isLoggedIn", "false")
                  localStorage.removeItem("userPhone")
                  window.dispatchEvent(new Event("loginStatusChanged"))
                } catch {}
                setIsLoggedIn(false)
                navigateToWindow("mobile-auth")
              }}
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: `
                  0 8px 32px rgba(1, 173, 239, 0.3),
                  0 4px 16px rgba(0, 0, 0, 0.2),
                  inset 0 2px 4px rgba(255, 255, 255, 0.3),
                  inset 0 -2px 4px rgba(0, 0, 0, 0.1)
                `,
                transform: 'translateY(0)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)'
                e.currentTarget.style.boxShadow = `
                  0 12px 40px rgba(1, 173, 239, 0.4),
                  0 6px 20px rgba(0, 0, 0, 0.3),
                  inset 0 2px 4px rgba(255, 255, 255, 0.4),
                  inset 0 -2px 4px rgba(0, 0, 0, 0.1),
                  0 0 20px rgba(1, 173, 239, 0.5)
                `
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)'
                e.currentTarget.style.boxShadow = `
                  0 8px 32px rgba(1, 173, 239, 0.3),
                  0 4px 16px rgba(0, 0, 0, 0.2),
                  inset 0 2px 4px rgba(255, 255, 255, 0.3),
                  inset 0 -2px 4px rgba(0, 0, 0, 0.1)
                `
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'translateY(1px) scale(0.98)'
                e.currentTarget.style.boxShadow = `
                  0 4px 16px rgba(1, 173, 239, 0.2),
                  0 2px 8px rgba(0, 0, 0, 0.2),
                  inset 0 2px 4px rgba(0, 0, 0, 0.2)
                `
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)'
                e.currentTarget.style.boxShadow = `
                  0 12px 40px rgba(1, 173, 239, 0.4),
                  0 6px 20px rgba(0, 0, 0, 0.3),
                  inset 0 2px 4px rgba(255, 255, 255, 0.4),
                  inset 0 -2px 4px rgba(0, 0, 0, 0.1),
                  0 0 20px rgba(1, 173, 239, 0.5)
                `
              }}
            >
              <span className="relative z-10">خروج</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform -skew-x-12 group-hover:translate-x-full" style={{ transition: 'opacity 0.3s, transform 0.6s' }} />
            </button>
          </>
        )}
      </div>

      {/* Pinned Windows Chips - Fixed at Top Center, Not Draggable - Hidden on Mobile */}
      {pinnedWindows.length > 0 && (
        <div className="hidden md:block fixed top-12 md:top-4 left-0 right-0 md:left-1/2 md:transform md:-translate-x-1/2 z-50 pointer-events-none px-2">
          <div className="flex gap-1.5 md:gap-2 justify-center max-w-full md:max-w-4xl overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide">
            {pinnedWindows.map((windowId) => {
              const window = visibleWindows.find((w) => w.id === windowId)
              if (!window) return null

              const position = pinnedWindowsPositions[windowId] || { x: 0, y: 0 }

              return (
                <div
                  key={windowId}
                  className="pointer-events-auto"
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px)`,
                    position: 'relative',
                  }}
                >
                  <button
                    onClick={() => {
                      navigateToPinnedWindow(windowId)
                    }}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    disabled={isNavigationLocked}
                    className={`
                      px-2 py-0.5 md:px-3 md:py-1 rounded-full text-[10px] md:text-xs font-medium transition-all duration-300 backdrop-blur-sm shadow-md whitespace-nowrap flex-shrink-0
                      ${
                        windowId === currentWindowId
                          ? "bg-[#01ADEF] text-white border border-white/30 shadow-[#01ADEF]/50"
                          : isNavigationLocked
                            ? "bg-gray-300/50 text-gray-400 border border-gray-400/30 cursor-not-allowed"
                            : "bg-white/80 text-[#08075C] border border-[#01ADEF]/20 hover:bg-[#01ADEF] hover:text-white hover:scale-105 cursor-pointer"
                      }
                    `}
                    title={isNavigationLocked ? "Navigation locked" : `Go to ${window.title}`}>
                  
                    <span className="inline-flex items-center gap-1">
                      {window.title}
                      {((window.requiresAuth && !isLoggedIn) || (window.id === "mobile-auth" && isLoggedIn)) && (
                        <Lock size={10} className="opacity-80" />
                      )}
                    </span>
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Desktop Navigation Arrows - Fixed at viewport center */}
      <div className="hidden md:block">
        <button
          onClick={navigateToPreviousWindow}
          disabled={isNavigationLocked}
          className={`
            fixed left-6 top-1/2 -translate-y-1/2 z-50
            w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-sm shadow-lg
            ${
              isNavigationLocked
                ? "bg-gray-300/50 text-gray-400 cursor-not-allowed"
                : "bg-[#01ADEF]/20 text-[#01ADEF] hover:bg-[#01ADEF] hover:text-white border border-[#01ADEF]/30 hover:scale-110"
            }
          `}
          title={isNavigationLocked ? "Navigation locked" : "Previous window (loops to last)"}
        >
          <ChevronLeft size={24} />
        </button>

        <button
          onClick={navigateToNextWindow}
          disabled={isNavigationLocked}
          className={`
            fixed right-6 top-1/2 -translate-y-1/2 z-50
            w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-sm shadow-lg
            ${
              isNavigationLocked
                ? "bg-gray-300/50 text-gray-400 cursor-not-allowed"
                : "bg-[#01ADEF]/20 text-[#01ADEF] hover:bg-[#01ADEF] hover:text-white border border-[#01ADEF]/30 hover:scale-110"
            }
          `}
          title={isNavigationLocked ? "Navigation locked" : "Next window (loops to first)"}
        >
          <ChevronRight size={24} />
        </button>
      </div>

      {/* Mobile chrome — bottom nav + current window label */}
      <div
        className="md:hidden fixed inset-x-0 bottom-0 z-50 pointer-events-none"
        style={{
          paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
        }}
      >
        <div className="pointer-events-none bg-gradient-to-t from-black/65 via-black/30 to-transparent pt-10 pb-2">
          <div className="pointer-events-auto mx-3 flex items-center gap-1.5 rounded-lg border border-white/[0.1] bg-[#161719] px-1.5 py-1.5 shadow-[0_10px_28px_rgba(0,0,0,0.35)] backdrop-blur-md">
            <button
              type="button"
              onClick={navigateToNextWindow}
              disabled={isNavigationLocked}
              aria-label="پنجره بعدی"
              className={`
                flex size-11 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 ease-out
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01ADEF]/55
                ${
                  isNavigationLocked
                    ? "cursor-not-allowed bg-white/[0.04] text-white/30"
                    : "bg-[rgba(1,173,239,0.16)] text-[#01ADEF] active:scale-95 active:bg-[#01ADEF] active:text-white"
                }
              `}
            >
              <ChevronRight size={22} />
            </button>

            <div className="min-w-0 flex-1 text-center px-1">
              <p className="truncate text-sm font-semibold text-white">
                {currentWindow?.title ?? "Pathway"}
              </p>
            </div>

            {currentWindow ? (
              <button
                type="button"
                onClick={() => togglePin(currentWindow.id)}
                aria-label={currentWindow.isPinned ? "برداشتن سنجاق" : "سنجاق کردن"}
                className={`
                  flex size-11 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 ease-out
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01ADEF]/55
                  ${
                    currentWindow.isPinned
                      ? "bg-[#01ADEF] text-white shadow-[0_4px_12px_rgba(1,173,239,0.3)]"
                      : "bg-white/[0.06] text-white/80 hover:bg-white/[0.1]"
                  }
                `}
              >
                {currentWindow.isPinned ? <PinOff size={16} /> : <Pin size={16} />}
              </button>
            ) : null}

            <button
              type="button"
              onClick={navigateToPreviousWindow}
              disabled={isNavigationLocked}
              aria-label="پنجره قبلی"
              className={`
                flex size-11 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 ease-out
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01ADEF]/55
                ${
                  isNavigationLocked
                    ? "cursor-not-allowed bg-white/[0.04] text-white/30"
                    : "bg-[rgba(1,173,239,0.16)] text-[#01ADEF] active:scale-95 active:bg-[#01ADEF] active:text-white"
                }
              `}
            >
              <ChevronLeft size={22} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Active Window */}
      <div className="flex items-center justify-center min-h-screen pt-14 pb-28 px-2 md:pt-14 md:pb-6 md:px-8 relative z-15 pointer-events-none">
       <div
          className={`bg-white/95 backdrop-blur-md rounded-xl md:rounded-3xl shadow-2xl border border-[#01ADEF]/20 max-w-4xl w-full overflow-hidden transition-all duration-600 ease-out relative pointer-events-auto ${
            isTransitioning ? "scale-90 opacity-70" : "scale-100 opacity-100"
          }`}
        >

          {CurrentWindowComponent && currentWindow && (
            <div className="relative">
              {(currentWindow.requiresAuth && !isLoggedIn) || (currentWindow.id === "mobile-auth" && isLoggedIn) ? (
                // Locked window placeholder - shows message instead of actual component
                <div className="min-h-[400px] flex flex-col items-center justify-center p-8" dir="rtl">
                  <div className="flex flex-col items-center gap-6 text-center">
                    <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center">
                      <Lock size={40} className="text-orange-500" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-2xl font-bold text-[#0c5492]">دسترسی محدود</h3>
                      <p className="text-gray-600 max-w-md">
                        برای دسترسی به <span className="font-semibold text-[#01ADEF]">{currentWindow.title}</span> باید وارد حساب کاربری خود شوید.
                      </p>
                    </div>
                    <button
                      onClick={() => navigateToWindow("mobile-auth")}
                      className="mt-4 px-6 py-3 rounded-lg font-medium text-white transition-all duration-300"
                      style={{
                        background: 'linear-gradient(135deg, #01ADEF 0%, #0088BB 100%)',
                        boxShadow: '0 4px 16px rgba(1, 173, 239, 0.3)',
                      }}
                    >
                      ورود به حساب کاربری
                    </button>
                  </div>
                </div>
              ) : (
                // Actual window component - only rendered when not locked
                // Use a flexible wrapper to handle different component prop signatures
                (() => {
                  const Component = CurrentWindowComponent as React.ComponentType<any>
                  return (
                    <Component
                      onContinue={() => {
                        if (currentWindow?.id === "mobile-auth") {
                          navigateToWindow("user-dashboard")
                        }
                      }}
                      onLockNavigation={setIsNavigationLocked}
                      onManageDomain={(domainId: string) => {
                        // Navigate to domain-management window and set the domain
                        navigateToWindow("domain-management")
                      }}
                      onNavigateToTutorial={(tutorialId: string) => {
                        // Navigate to tutorial window and pre-select the tutorial
                        navigateToWindow("tutorial")
                        setPendingTutorialId(tutorialId)
                      }}
                      initialTutorialId={currentWindow?.id === "tutorial" ? pendingTutorialId : undefined}
                    />
                  )
                })()
              )}
            </div>
          )}

          {currentWindow && (
            <div className="absolute top-4 left-4 hidden md:block">
              <button
                onClick={() => togglePin(currentWindow.id)}
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-sm shadow-lg
                  ${
                    currentWindow.isPinned
                      ? "bg-[#01ADEF] text-white shadow-[#01ADEF]/50 border-2 border-white/20 hover:bg-[#0194D1]"
                      : "bg-white/20 text-[#08075C] hover:bg-[#01ADEF] hover:text-white border-2 border-[#01ADEF]/30"
                  }
                  hover:scale-110 active:scale-95
                `}
                title={currentWindow.isPinned ? "Unpin this window" : "Pin this window"}
              >
                {currentWindow.isPinned ? <PinOff size={16} /> : <Pin size={16} />}
              </button>
            </div>
          )}
        </div>
      </div>


      {/* The chatbot bubble, and the sign-up wizard when one is pending. */}
      <OnboardingWidgetHost />

      {/* Lock Indicator */}
      {isNavigationLocked && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-30">
          <div className="bg-red-500/90 backdrop-blur-sm rounded-full px-6 py-3 text-white text-sm font-semibold shadow-lg flex items-center gap-2">
            <Lock size={16} />
            Navigation locked during quiz
          </div>
        </div>
      )}
    </div>
  )
}